import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PatientChatChannelType } from 'helpers/config/constants';
import { FCWithChildren } from 'helpers/types/FCWithChidren';
import { useAuthentication } from 'hooks/useAuthentication';
import useStreamClient from 'hooks/useStreamClient';
import { Channel, DefaultGenerics, Event, StreamChat } from 'stream-chat';

import { useCurrentUser } from './CurrentUserContext';

export interface ChatContext {
  streamClient?: StreamChat<DefaultGenerics>;
  channels?: Channel<DefaultGenerics>[];
  userConnected?: boolean;
  hasConsultAndNeedsVerification?: boolean;
  reloadChannels: () => void;
  totalUnread?: number;
}

const ChatContext = createContext<ChatContext>({
  reloadChannels: () => undefined,
});

const eventsRequireReload = [
  'notification.added_to_channel',
  'notification.mark_read',
  'notification.message_new',
  'notification.removed_from_channel',
  'message.new',
];

export const ChatProvider: FCWithChildren<{}> = ({ children }) => {
  const {
    state: { account },
  } = useCurrentUser();
  const { user } = useAuthentication();

  const { chatClient: streamClient, clientId } = useStreamClient(
    account ?? undefined,
  );

  const [channels, setChannels] = useState<Channel<DefaultGenerics>[]>([]);

  const [totalUnread, setTotalUnread] = useState<number>(
    (streamClient?.user?.unread_count as number) ?? 0,
  );

  // This should ideally be in a context of its own, but we've already got a lot
  // of App level contexts and for a single value it seems a bit silly
  // Essentially, the verification requirement only shows up if there is at least
  // one consult, and the user has not verified their email already
  const hasConsultAndNeedsVerification = useMemo(() => {
    return !channels.reduce(() => (user?.email_verified ? 1 : 0), 1);
  }, [channels, user?.email_verified]);

  const reloadChannels = useCallback(() => {
    if (!account || !streamClient?.user) {
      setChannels([]);
      return;
    }

    const channelsRequest = streamClient.queryChannels({
      type: PatientChatChannelType,
      members: { $in: [account?.streamChatId || ''] },
    });

    channelsRequest.then(setChannels);
  }, [account, streamClient]);

  const onSteamEvent = useCallback(
    (event: Event) => {
      if (!account || !streamClient?.user) return;

      if (event.type === 'connection.changed' && event.online) {
        reloadChannels();
        return;
      }

      if (eventsRequireReload.includes(event.type)) {
        reloadChannels();
        const unreads = channels.reduce(
          (prev, next) => prev + next.countUnread(),
          0,
        );
        setTotalUnread(unreads);
      }
    },
    [account, channels, reloadChannels, streamClient?.user],
  );

  useEffect(() => {
    if (!clientId) return;

    streamClient.on(onSteamEvent);

    if (streamClient.user?.unread_count) {
      setTotalUnread(streamClient.user?.unread_count as number);
    }

    return () => streamClient.off(onSteamEvent);
  }, [
    clientId,
    channels,
    onSteamEvent,
    reloadChannels,
    streamClient,
    streamClient?.user,
    streamClient?.user?.unread_count,
  ]);

  useEffect(() => {
    if (!clientId) return;

    reloadChannels();
  }, [clientId, reloadChannels]);

  const context: ChatContext = {
    streamClient,
    channels,
    hasConsultAndNeedsVerification,
    reloadChannels,
    totalUnread,
  };

  return (
    <ChatContext.Provider value={context}>{children}</ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext<ChatContext>(ChatContext);

  if (!context)
    throw new Error('!--- useChat must be used inside a ChatProvider ---!');

  return context;
};
